import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { AppHeader } from "@/components/AppHeader";
import { SupportButton } from "@/components/SupportButton";
import { withRowKeys } from "@/lib/rowKey";
import { logError } from "@/lib/log";
import { SorterDownloads } from "@/components/SorterDownloads";
import { ProjectInfo } from "@/components/project/ProjectInfo";
import { ProjectMilestones } from "@/components/project/ProjectMilestones";
import { ProjectSettingsForm } from "@/components/project/ProjectSettingsForm";
import { ProjectStats, ProjectActions } from "@/components/project/ProjectActions";
import { ChargesPanel, InvoicePanel } from "@/components/project/ChargesPanel";
import { SelectionSummary } from "@/components/project/SelectionSummary";

const MILESTONES = ["selection_received", "editing_complete", "printing_complete", "delivered"];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState("");
  const [charge, setCharge] = useState({ label: "", amount: "" });
  const [accessCode, setAccessCode] = useState(null);
  const [printCatalog, setPrintCatalog] = useState({});

  useEffect(() => {
    api.get("/studio")
      .then((r) => setPrintCatalog(r.data.pricing?.print_prices || {}))
      .catch((err) => logError("Could not load print catalog", err));
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}`);
      setProject(res.data);
      setForm({
        code: res.data.code || "",
        title: res.data.title,
        client_name: res.data.client_name || "",
        client_email: res.data.client_email || "",
        client_phone: res.data.client_phone || "",
        extra_edit_price: res.data.pricing_override?.extra_edit_price ?? "",
        package_price: res.data.package_price ?? "",
        package_label: res.data.package_label ?? "",
        drive_link: res.data.drive_link || "",
        edit_quota: res.data.edit_quota,
        allow_extra_edits: res.data.allow_extra_edits ?? true,
        max_extra_edits: res.data.max_extra_edits ?? 0,
        print_sizes: (res.data.print_sizes || []),
        print_quota_items: withRowKeys(res.data.print_quota_items || []),
        module: res.data.module || "edit_print",
      });
    } catch (err) {
      toast.error(apiError(err, "Project not found"));
      navigate("/dashboard");
    }
  }, [projectId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      const res = await api.put(`/projects/${projectId}`, {
        ...form,
        client_email: form.client_email || null,
        edit_quota: Number(form.edit_quota),
        max_extra_edits: Number(form.max_extra_edits),
        pricing_override: form.extra_edit_price === "" ? {} : { extra_edit_price: Number(form.extra_edit_price) },
        package_price: form.package_price === "" || form.package_price == null ? null : Number(form.package_price),
        print_sizes: form.print_sizes,
        print_quota_items: form.print_quota_items.filter((i) => i.size).map(({ _k, ...i }) => ({ ...i, qty: Number(i.qty) })),
      });
      setProject(res.data);
      toast.success(t("saved_ok"));
    } catch (err) {
      toast.error(apiError(err, "Could not save"));
    } finally {
      setBusy("");
    }
  };

  const action = async (name, fn, msg) => {
    setBusy(name);
    try {
      const res = await fn();
      toast.success(msg);
      await load();
      return res;
    } catch (err) {
      toast.error(apiError(err, "Action failed"));
    } finally {
      setBusy("");
    }
  };

  const toggleMilestone = async (milestone) => {
    const done = !project.milestones?.[milestone];
    const res = await action("ms", () => api.post(`/projects/${projectId}/milestone`, { milestone, done }), t("order_status"));
    const url = res?.data?.whatsapp_url;
    if (url) window.open(url, "_blank", "noopener");
  };

  const sendWhatsapp = async (kind, milestone) => {
    try {
      const res = await api.get(`/projects/${projectId}/whatsapp`, { params: { kind, milestone } });
      if (res.data.url) window.open(res.data.url, "_blank", "noopener");
      else toast.error(t("no_phone"));
    } catch (err) {
      toast.error(apiError(err, t("no_phone")));
    }
  };

  const addCharge = async (e) => {
    e.preventDefault();
    if (!charge.label || !charge.amount) return;
    await action("charge", () => api.post(`/projects/${projectId}/charges`, {
      label: charge.label, amount: Number(charge.amount),
    }), t("add_charge"));
    setCharge({ label: "", amount: "" });
  };

  const updatePrint = async (fileId, index, patch) => {
    // Prints are a flat list, so target the Nth entry for this photo rather than all of them.
    let seen = -1;
    const prints = (project.summary.prints || []).map((p) => {
      if (p.file_id !== fileId) return p;
      seen += 1;
      return seen === index ? { ...p, ...patch } : p;
    });
    const res = await api.put(`/projects/${projectId}/prints`, { prints });
    setProject(res.data);
  };

  const removePrint = async (fileId, index) => {
    let seen = -1;
    const prints = (project.summary.prints || []).filter((p) => {
      if (p.file_id !== fileId) return true;
      seen += 1;
      return seen !== index;
    });
    const res = await api.put(`/projects/${projectId}/prints`, { prints });
    setProject(res.data);
    toast.success(t("print_removed"));
  };

  const addPrint = async (fileId, size) => {
    const prints = [...(project.summary.prints || []), { file_id: fileId, size, qty: 1, frame_code: "", note: "" }];
    const res = await api.put(`/projects/${projectId}/prints`, { prints });
    setProject(res.data);
    toast.success(t("print_added"));
  };

  const saveDiscounts = async (lines) => {
    setBusy("discount");
    try {
      const res = await api.put(`/projects/${projectId}/discounts`, { lines });
      setProject(res.data);
      toast.success(t("discount_saved"));
    } catch (err) {
      toast.error(apiError(err, "Could not apply discount"));
    } finally {
      setBusy("");
    }
  };

  const resetCode = async () => {
    const res = await api.post(`/studio/clients/${projectId}/reset-code`);
    setAccessCode(res.data.access_code);
    toast.success(`${t("access_code_label")}: ${res.data.access_code}`);
  };

  if (!project || !form)
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#707070]">
        <Loader2 className="animate-spin" />
      </div>
    );

  const clientLink = `${window.location.origin}/g/${project.share_token}`;
  const s = project.summary;
  const invoice = project.invoice;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain" data-testid="project-detail-page">
      <AppHeader />

      <main className="max-w-7xl mx-auto px-5 lg:px-12 py-10 lg:py-14 space-y-12">
        <ProjectInfo project={project} clientLink={clientLink} accessCode={accessCode}
          onBack={() => navigate("/dashboard")}
          onCopy={() => { navigator.clipboard.writeText(clientLink); toast.success("Copied"); }}
          onWhatsapp={sendWhatsapp} onResetCode={resetCode} />

        <ProjectMilestones milestones={project.milestones} names={MILESTONES}
          clientPhone={project.client_phone} busy={busy === "ms"}
          onToggle={toggleMilestone} onWhatsapp={sendWhatsapp} />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <ProjectSettingsForm form={form} setForm={setForm} busy={busy} onSubmit={save} printCatalog={printCatalog} />

          <div className="lg:col-span-2 space-y-6">
            <ProjectStats project={project} summary={s} busy={busy}
              onSync={() => action("sync", () => api.post(`/projects/${projectId}/sync-drive`), t("resync"))} />

            <SorterDownloads path={`/projects/${projectId}/sorter/phase1`}
              filePrefix={`${(project.code || project.title || "project").replace(/[^\w-]+/g, "_").replace(/^_+|_+$/g, "")}_phase1`}
              titleKey="sorter_phase1_title" bodyKey="sorter_phase1_body" testIdPrefix="sorter-phase1"
              previewProjectId={projectId} />

            <ProjectActions project={project} busy={busy}
              pdfHref={`${API}/gallery/${project.share_token}/summary.pdf`}
              onEmail={() => action("email", () => api.post(`/projects/${projectId}/email-summary`), t("email_summary"))}
              onReopen={() => action("reopen", () => api.post(`/projects/${projectId}/reopen`), t("reopen"))} />

            <ChargesPanel charges={project.charges} charge={charge} setCharge={setCharge} onAdd={addCharge}
              onRemove={(chargeId) => action("charge", () => api.delete(`/projects/${projectId}/charges/${chargeId}`), "Removed")} />

            {isOwner && invoice && !invoice.hidden && (
              <InvoicePanel key={JSON.stringify(project.discounts || project.discount || {})}
                invoice={invoice}
                discounts={project.discounts ?? (project.discount?.amount ? [project.discount] : [])}
                onSaveDiscounts={saveDiscounts} busy={busy === "discount"} />
            )}
          </div>
        </div>

        {s.edit_count > 0 && (
          <SelectionSummary summary={s} printSizes={project.print_sizes}
            onUpdatePrint={updatePrint} onRemovePrint={removePrint} onAddPrint={addPrint} />
        )}
      </main>
      <SupportButton />
    </div>
  );
}
